/** Keep the JSON body below 4 MB: even an outer base64-encoded Lambda
 * event body (4/3 expansion) leaves >650 kB below the decimal 6 MB limit
 * for headers/context. The actual UTF-8 JSON is checked again before signing.
 */
export const BRAND_GUIDELINE_REQUEST_BYTES = 4_000_000;
const MAX_IMAGES = 10;
const MAX_EDGE = 1600;
const MIN_EDGE = 512;
const MAX_SOURCE_BYTES = 32 * 1024 * 1024;
const MAX_SOURCE_PIXELS = 40_000_000;
const TIMEOUT_MS = 30_000;

export interface BrandGuidelineImage {
  name: string;
  type: string;
  size: number;
  data: string;
}

export function serializeBrandGuidelineRequest(images: BrandGuidelineImage[]): string {
  if (!images.length || images.length > MAX_IMAGES) {
    throw new Error("Select between 1 and 10 benchmark images.");
  }
  const body = JSON.stringify({ images });
  if (new TextEncoder().encode(body).byteLength > BRAND_GUIDELINE_REQUEST_BYTES) {
    throw new Error("The benchmark request is too large. Please use fewer or smaller images and try again.");
  }
  return body;
}

function sourceBlob(item: unknown, index: number): Blob {
  const entry = item as { file?: unknown; source?: unknown } | null;
  const source = item instanceof Blob ? item : entry?.file ?? entry?.source;
  if (!(source instanceof Blob) || !source.size) {
    throw new Error(`Benchmark image ${index + 1} is not a readable file. Remove it and select it again.`);
  }
  return source;
}

function decodeImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const timer = setTimeout(() => finish(new Error("Image decoding timed out.")), TIMEOUT_MS);
    const finish = (error?: Error) => {
      clearTimeout(timer);
      image.onload = null;
      image.onerror = null;
      if (error) {
        image.src = "";
        reject(error);
      } else resolve(image);
    };
    image.onload = () => finish();
    image.onerror = () => finish(new Error("The browser could not decode this image. Re-export it as PNG or JPEG."));
    image.src = url;
  });
}

function encodeCanvas(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Image compression timed out.")), TIMEOUT_MS);
    try {
      canvas.toBlob((blob) => {
        clearTimeout(timer);
        if (!blob || !blob.size) reject(new Error("The browser could not compress this image."));
        else if (!["image/png", "image/jpeg"].includes(blob.type)) reject(new Error("The browser returned an unsupported image format."));
        else resolve(blob);
      }, type, quality);
    } catch (error) {
      clearTimeout(timer);
      reject(error);
    }
  });
}

function readBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const timer = setTimeout(() => { reader.abort(); reject(new Error("Reading the compressed image timed out.")); }, TIMEOUT_MS);
    const fail = () => { clearTimeout(timer); reject(new Error("Could not read the compressed image. Please select it again.")); };
    reader.onerror = fail;
    reader.onabort = fail;
    reader.onload = () => {
      clearTimeout(timer);
      const result = reader.result;
      if (typeof result !== "string" || !result.startsWith(`data:${blob.type};base64,`)) return fail();
      resolve(result.slice(result.indexOf(",") + 1));
    };
    try { reader.readAsDataURL(blob); } catch (error) { clearTimeout(timer); reject(error); }
  });
}

async function compressImage(source: Blob, budget: number): Promise<Blob | null> {
  if (source.size > MAX_SOURCE_BYTES) throw new Error("Use a source image smaller than 32 MB.");
  const url = URL.createObjectURL(source);
  let image: HTMLImageElement | undefined;
  let canvas: HTMLCanvasElement | undefined;
  try {
    image = await decodeImage(url);
    const { naturalWidth: width, naturalHeight: height } = image;
    if (!width || !height || width * height > MAX_SOURCE_PIXELS) {
      throw new Error("Use an image with valid dimensions below 40 megapixels.");
    }
    canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Image compression is unavailable in this browser. Please try another browser.");
    const originalEdge = Math.max(width, height);
    let edge = Math.min(MAX_EDGE, originalEdge);
    const minimumEdge = Math.min(MIN_EDGE, originalEdge);
    let transparent = false;
    for (let round = 0; round < 6; round++) {
      const scale = edge / originalEdge;
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      if (round === 0) {
        // Inspect actual alpha, not the filename/MIME. Preserve transparency as
        // PNG (never flatten onto black/white); scan strips to bound readback RAM.
        for (let y = 0; y < canvas.height && !transparent; y += 32) {
          const pixels = context.getImageData(0, y, canvas.width, Math.min(32, canvas.height - y)).data;
          for (let i = 3; i < pixels.length; i += 4) {
            if (pixels[i] < 255) { transparent = true; break; }
          }
        }
      }
      const type = transparent ? "image/png" : "image/jpeg";
      for (const quality of transparent ? [1] : [0.88, 0.72, 0.56]) {
        const blob = await encodeCanvas(canvas, type, quality);
        if (transparent && blob.type !== "image/png") throw new Error("This browser cannot preserve image transparency.");
        if (blob.size <= budget) return blob;
        // PNG ignores quality, including browsers falling back from JPEG.
        if (blob.type === "image/png") break;
      }
      if (edge === minimumEdge) break;
      edge = Math.max(minimumEdge, Math.floor(edge * 0.75));
    }
    // A provisional share is not a hard per-image limit. Let the batch
    // allocator retry after discovering spare capacity in smaller references.
    return null;
  } finally {
    if (canvas) { canvas.width = 0; canvas.height = 0; }
    if (image) image.src = "";
    URL.revokeObjectURL(url);
  }
}

/** Produce upload-only derivatives, sequentially: never mutate FilePond's originals
 * or hold a batch of decoded bitmaps/original base64 strings in memory.
 * Each pass uses a fixed fair share; unused capacity is redistributed to
 * deferred images on the next pass, independent of selection order.
 * Any failed file aborts the entire batch, never silently changes the references.
 */
export async function prepareBrandGuidelineImages(items: readonly unknown[]): Promise<BrandGuidelineImage[]> {
  if (!items.length || items.length > MAX_IMAGES) throw new Error("Select between 1 and 10 benchmark images.");
  const sources = items.map(sourceBlob);
  const metadata = sources.map((source, index) => ({
    name: source instanceof File ? source.name : `benchmark-${index + 1}`,
    type: "image/jpeg", size: 99999999, data: "",
  }));
  // Reserve the full metadata (including UTF-8 names), size digits, JSON syntax,
  // and a small rounding cushion before assigning each image its base64 share.
  let remaining = BRAND_GUIDELINE_REQUEST_BYTES - new TextEncoder().encode(serializeBrandGuidelineRequest(metadata)).byteLength - 1024;
  const blobs: Blob[] = new Array(sources.length);
  let pending = sources.map((_, index) => index);
  while (pending.length) {
    // Freeze the share for this entire pass: otherwise an image's budget
    // still depends on whether a small reference happens to precede it.
    const budget = Math.floor(Math.floor(remaining / pending.length) / 4) * 3;
    if (budget <= 0) throw new Error("The image names are too large. Rename the files and try again.");
    const deferred: number[] = [];
    for (const index of pending) {
      try {
        const blob = await compressImage(sources[index], budget);
        if (!blob) {
          deferred.push(index);
          continue;
        }
        blobs[index] = blob;
        remaining -= 4 * Math.ceil(blob.size / 3);
      } catch (error) {
        throw new Error(`Could not prepare "${metadata[index].name}": ${error instanceof Error ? error.message : "Please select the image again."}`);
      }
    }
    // Every productive pass removes at least one pending image; at most ten
    // passes. No progress means there is no more unused capacity to reclaim.
    if (deferred.length === pending.length) {
      throw new Error(`Could not prepare "${metadata[deferred[0]].name}": Could not compress this benchmark enough without making it too small. Use fewer images or a smaller source image.`);
    }
    pending = deferred;
  }
  const result: BrandGuidelineImage[] = [];
  for (let index = 0; index < sources.length; index++) {
    try {
      const blob = blobs[index];
      const data = await readBase64(blob);
      result.push({ name: metadata[index].name, type: blob.type, size: blob.size, data });
    } catch (error) {
      throw new Error(`Could not prepare "${metadata[index].name}": ${error instanceof Error ? error.message : "Please select the image again."}`);
    }
  }
  serializeBrandGuidelineRequest(result);
  return result;
}
